import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaPhone,
  FaCalendarAlt,
  FaGraduationCap,
  FaHome,
  FaCamera,
  FaArrowRight,
  FaArrowLeft,
  FaCheckCircle,
  FaSkating,
  FaTimesCircle
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { studentAPI } from '../services/api';
import logo from '../assets/logo.png';
import './StudentRegisterPage.css';

function StudentRegisterPage() {
  const { loginUser, handleLoginSuccess, isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    FirstName: '',
    LastName: '',
    DOB: '',
    Gender: 'M',
    FatherName: '',
    MotherName: '',
    SchoolName: '',
    SchoolGrade: '',
    Address: '',
    ContactNumber: '',
    EmergencyContact: '',
    SkillLevel: 'Beginner',
    RegistrationDate: new Date().toISOString().split('T')[0],
    Email: '',
    Password: '',
    ConfirmPassword: '',
    TermsAccepted: false
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errorMessage) setErrorMessage('');
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrorMessage('Please upload a valid image file (PNG, JPG, JPEG).');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage('Image size should be less than 5MB.');
        return;
      }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
      if (errorMessage) setErrorMessage('');
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
      setPhotoPreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!formData.FirstName.trim() || !formData.LastName.trim()) {
      setErrorMessage('Please provide both First Name and Last Name.');
      return;
    }

    if (!formData.ContactNumber.trim()) {
      setErrorMessage('Please enter a valid Contact Number.');
      return;
    }

    if (!formData.Email.trim()) {
      setErrorMessage('Please provide an Email Address.');
      return;
    }

    if (formData.Password.length < 6) {
      setErrorMessage('Password must be at least 6 characters long.');
      return;
    }

    if (formData.Password !== formData.ConfirmPassword) {
      setErrorMessage('Passwords do not match. Please verify your password.');
      return;
    }

    if (!formData.TermsAccepted) {
      setErrorMessage('Please accept the Terms & Conditions to complete your registration.');
      return;
    }

    setLoading(true);

    try {
      // Step 1: Build multipart student data according to POST /api/students
      const studentData = new FormData();
      studentData.append('FirstName', formData.FirstName.trim());
      studentData.append('LastName', formData.LastName.trim());
      if (formData.DOB) studentData.append('DOB', formData.DOB);
      studentData.append('Gender', formData.Gender || 'M');
      if (formData.FatherName) studentData.append('FatherName', formData.FatherName.trim());
      if (formData.MotherName) studentData.append('MotherName', formData.MotherName.trim());
      if (formData.SchoolName) studentData.append('SchoolName', formData.SchoolName.trim());
      if (formData.SchoolGrade) studentData.append('SchoolGrade', formData.SchoolGrade.trim());
      if (formData.Address) studentData.append('Address', formData.Address.trim());
      studentData.append('ContactNumber', formData.ContactNumber.trim());
      if (formData.EmergencyContact) studentData.append('EmergencyContact', formData.EmergencyContact.trim());
      studentData.append('SkillLevel', formData.SkillLevel || 'Beginner');
      if (formData.RegistrationDate) studentData.append('RegistrationDate', formData.RegistrationDate);
      studentData.append('TermsAccepted', formData.TermsAccepted ? 'true' : 'false');

      if (formData.Email) {
        studentData.append('email', formData.Email.trim());
        studentData.append('Email', formData.Email.trim());
      }
      if (formData.Password) {
        studentData.append('password', formData.Password);
        studentData.append('Password', formData.Password);
      }
      if (formData.ConfirmPassword) {
        studentData.append('confirmPassword', formData.ConfirmPassword);
        studentData.append('ConfirmPassword', formData.ConfirmPassword);
      }

      if (photoFile instanceof File) {
        studentData.append('PhotoPath', photoFile);
      }

      // Step 2: Call student creation API (POST /api/students)
      const res = await studentAPI.create(studentData);

      // Step 3: Handle authentication & redirection to dashboard
      if (isAuthenticated && isAdmin) {
        setSuccessMessage('Student added successfully by Admin.');
        setTimeout(() => navigate('/students'), 1200);
        return;
      }

      let loggedIn = false;
      if (res?.data?.token) {
        const fullUser = {
          ...(res.data.user || {}),
          StudentID: res.data.StudentID || res.data.student?.StudentID || res.data.user?.StudentID,
          student: res.data.student,
          FirstName: res.data.student?.FirstName || formData.FirstName,
          LastName: res.data.student?.LastName || formData.LastName,
          PhotoPath: res.data.PhotoPath || res.data.student?.PhotoPath || ''
        };

        handleLoginSuccess({
          token: res.data.token,
          user: fullUser,
          role: res.data.user?.Role || 'user'
        });
        loggedIn = true;
      } else if (formData.Email && formData.Password) {
        try {
          await loginUser(formData.Email.trim(), formData.Password);
          loggedIn = true;
        } catch (loginErr) {
          console.warn('Auto-login error:', loginErr);
        }
      }

      setSuccessMessage('Student Registration Successful! Redirecting to your dashboard...');

      setTimeout(() => {
        if (loggedIn) {
          navigate('/dashboard', {
            state: {
              focusTab: 'profile',
              welcomeStudent: true
            }
          });
        } else {
          navigate('/login', {
            state: {
              message: 'Registration successful! Please log in with your credentials.'
            }
          });
        }
      }, 1200);

    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Registration failed. Please check your details and try again.';
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="student-register-shell">
      <div className="blur-circle circle-1" />
      <div className="blur-circle circle-2" />

      <motion.div
        className="student-register-card"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55 }}
      >
        <div className="student-register-top-bar">
          <Link to="/login" className="header-back-btn" title="Back to Login">
            <FaArrowLeft /> <span>Back</span>
          </Link>
          <span className="badge-tag">
            <FaSkating /> Epsilon Skating Club
          </span>
        </div>

        <div className="student-register-header">
          <img src={logo} alt="Epsilon Skating Club Logo" />
          <div className="header-text">
            <h2>Student Registration</h2>
            <p>Fill out the details below to enroll as a student and create your account.</p>
          </div>
        </div>

        {errorMessage && (
          <div className="alert-message alert-error">
            <FaTimesCircle /> {errorMessage}
          </div>
        )}

        {successMessage && (
          <div className="alert-message alert-success">
            <FaCheckCircle /> {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="student-register-form">
          {/* Section 1: Personal Details */}
          <div className="form-section-title">
            <FaUser /> Personal Details
          </div>

          <div className="form-grid-2">
            <div className="field-group">
              <label>
                First Name <span className="req">*</span>
              </label>
              <input
                type="text"
                name="FirstName"
                placeholder="e.g. Rahul"
                value={formData.FirstName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="field-group">
              <label>
                Last Name <span className="req">*</span>
              </label>
              <input
                type="text"
                name="LastName"
                placeholder="e.g. Sharma"
                value={formData.LastName}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-grid-2">
            <div className="field-group">
              <label>
                <FaCalendarAlt /> Date of Birth
              </label>
              <input
                type="date"
                name="DOB"
                value={formData.DOB}
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="field-group">
              <label>Gender</label>
              <select name="Gender" value={formData.Gender} onChange={handleChange}>
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Section 2: Family & Education */}
          <div className="form-section-title">
            <FaGraduationCap /> Family & School Information
          </div>

          <div className="form-grid-2">
            <div className="field-group">
              <label>Father's Name</label>
              <input
                type="text"
                name="FatherName"
                placeholder="Father's full name"
                value={formData.FatherName}
                onChange={handleChange}
              />
            </div>

            <div className="field-group">
              <label>Mother's Name</label>
              <input
                type="text"
                name="MotherName"
                placeholder="Mother's full name"
                value={formData.MotherName}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-grid-2">
            <div className="field-group">
              <label>School Name</label>
              <input
                type="text"
                name="SchoolName"
                placeholder="Current school name"
                value={formData.SchoolName}
                onChange={handleChange}
              />
            </div>

            <div className="field-group">
              <label>School Grade / Class</label>
              <input
                type="text"
                name="SchoolGrade"
                placeholder="e.g. 5th Standard"
                value={formData.SchoolGrade}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Section 3: Contact & Address */}
          <div className="form-section-title">
            <FaHome /> Contact & Address
          </div>

          <div className="field-group">
            <label>Full Residential Address</label>
            <textarea
              name="Address"
              placeholder="Enter complete address..."
              value={formData.Address}
              onChange={handleChange}
              rows="2"
            />
          </div>

          <div className="form-grid-2">
            <div className="field-group">
              <label>
                <FaPhone /> Contact Number <span className="req">*</span>
              </label>
              <input
                type="tel"
                name="ContactNumber"
                placeholder="Primary phone number"
                value={formData.ContactNumber}
                onChange={handleChange}
                required
              />
            </div>

            <div className="field-group">
              <label>
                <FaPhone /> Emergency Contact
              </label>
              <input
                type="tel"
                name="EmergencyContact"
                placeholder="Emergency contact number"
                value={formData.EmergencyContact}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Section 4: Skating Preferences & Photo */}
          <div className="form-section-title">
            <FaSkating /> Skating Preferences & Photo
          </div>

          <div className="form-grid-2">
            <div className="field-group">
              <label>Skill Level</label>
              <select name="SkillLevel" value={formData.SkillLevel} onChange={handleChange}>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>

            <div className="field-group">
              <label>
                <FaCalendarAlt /> Registration Date
              </label>
              <input
                type="date"
                name="RegistrationDate"
                value={formData.RegistrationDate}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="field-group">
            <label>
              <FaCamera /> Student Photo
            </label>
            <div className="photo-upload-container">
              <div className="photo-preview-box">
                {photoPreview ? (
                  <img src={photoPreview} alt="Student Preview" />
                ) : (
                  <FaUser />
                )}
              </div>
              <div className="photo-upload-meta">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  id="student-photo-input"
                />
                <p className="photo-help">Upload a clear square photo (JPG, PNG). Max 5MB.</p>
                {photoPreview && (
                  <button
                    type="button"
                    onClick={removePhoto}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: 0,
                      marginTop: '4px'
                    }}
                  >
                    Remove Photo
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Section 5: Account Credentials */}
          <div className="form-section-title">
            <FaLock /> Account Credentials (For Login)
          </div>

          <div className="field-group">
            <label>
              <FaEnvelope /> Email Address <span className="req">*</span>
            </label>
            <input
              type="email"
              name="Email"
              placeholder="e.g. student@example.com"
              value={formData.Email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-grid-2">
            <div className="field-group">
              <label>
                <FaLock /> Password <span className="req">*</span>
              </label>
              <input
                type="password"
                name="Password"
                placeholder="At least 6 characters"
                value={formData.Password}
                onChange={handleChange}
                required
              />
            </div>

            <div className="field-group">
              <label>
                <FaLock /> Confirm Password <span className="req">*</span>
              </label>
              <input
                type="password"
                name="ConfirmPassword"
                placeholder="Re-enter password"
                value={formData.ConfirmPassword}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Section 6: Terms & Agreement */}
          <label className="checkbox-container">
            <input
              type="checkbox"
              name="TermsAccepted"
              checked={formData.TermsAccepted}
              onChange={handleChange}
              required
            />
            <span>
              I accept the club <strong>Terms & Conditions</strong> and confirm that all details provided are true and accurate.
            </span>
          </label>

          <button
            type="submit"
            className="btn-submit-registration"
            disabled={loading}
          >
            {loading ? (
              'Creating Account...'
            ) : (
              <>
                Create Account <FaArrowRight />
              </>
            )}
          </button>
        </form>

        <div className="auth-footer-student">
          <p>
            Already have an account? <Link to="/login">Login here</Link>
          </p>
          {/* <p style={{ marginTop: '6px', fontSize: '0.82rem' }}>
            Looking for Admin Portal? <Link to="/admin/login">Admin Login</Link>
          </p> */}
        </div>
      </motion.div>
    </div>
  );
}

export default StudentRegisterPage;
